import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { executeTrade } from "../api/api";
import { calcStopLoss, calcTakeProfit } from "../utils/calculations";
import { roundToStepNumber } from "../utils/lotUtils";
import { Button } from "./ui/button";
import { Loader2, SendHorizontal } from "lucide-react";

const TradeExecution = ({ 
    selectedAccount, 
    selectedSymbol, 
    tradeDirection, 
    lotSize, 
    bidPrice, 
    askPrice, 
    stopLossMode, 
    stopLossValue, 
    riskPercent, 
    pipSize, 
    stopLossDistance,
    takeProfitValue,
    rrRatio,
    projectedProfit,   
    projectedLoss,  
    tpMode,
    takeProfitPrice,
    atrMultiplier,
    stopLossType,
    reason,
    setShowJournalModal,
    setExecutedTradeId,
    setPriceFeedPaused,
    // takeProfitDistance (deprecated: compute locally)
    orderType,
    limitEntryPrice,
    profitMode,
    targets,
    lotStep,
    minLot,
    maxLot,
        compact = false,
}) => {
    const [loading, setLoading] = useState(false);
    const [, setFinalSlDistance] = useState(null);
    const isSell = tradeDirection === "SELL";
    const actionLabel = isSell ? "Execute Sell" : "Execute Buy";
        const buttonClassName = compact
                ? `min-h-[40px] w-full border text-[13px] font-semibold shadow-[0_4px_12px_rgba(0,0,0,0.18)] transition-all duration-150 hover:-translate-y-0.5 ${
                        isSell ? "border-destructiveHover" : "border-successHover"
                    }`
                : `mt-4 min-h-[58px] w-full border-2 text-base font-bold shadow-[0_10px_24px_rgba(0,0,0,0.28)] transition-all duration-150 hover:-translate-y-0.5 ${
                        isSell ? "border-destructiveHover" : "border-successHover"
                    }`;
        const buttonSize = compact ? "sm" : "lg";
        const iconClasses = compact ? "mr-1.5 h-3.5 w-3.5" : "mr-2 h-5 w-5";

    useEffect(() => {
        

        if (!stopLossValue || stopLossValue === "") {
            setFinalSlDistance(null);
            return;
        }
    
        const { slDistance } = calcStopLoss({
            stopLossMode,
            stopLossValue,
            tradeDirection,
            askPrice,
            bidPrice,
            pipSize,
            entryPrice: orderType === "LIMIT" ? parseFloat(limitEntryPrice) : null,
        });
    
        console.log("Calculated Stop Loss Distance:", slDistance);  
    
        setFinalSlDistance(slDistance !== "N/A" ? parseFloat(slDistance) : null);
    }, [stopLossMode, stopLossValue, tradeDirection, askPrice, bidPrice, pipSize, orderType, limitEntryPrice]);
    
    useEffect(() => {
        
    }, [stopLossDistance]);

    const handleTrade = async () => {
        
        if (setPriceFeedPaused) {
            setPriceFeedPaused(true);
          }
        console.log("Trade Execution Received TP Mode:", tpMode);
        console.log("Using finalTpPrice from UI:", takeProfitPrice);  // ✅ Debug log
        const entryPrice = orderType === "LIMIT"
        ? parseFloat(limitEntryPrice)
        : parseFloat(tradeDirection === "BUY" ? askPrice : bidPrice);
        
        // Determine the take_profit value for the payload
        let tpValueForPayload; 

        if (profitMode === 'multi') {
            tpValueForPayload = 0; // Default to 0 for multi-TP if specific target price isn't found/valid
        
            if (targets && targets.length > 0) {
                const sortedTargets = [...targets].sort((a, b) => a.rank - b.rank);
                const lastTarget = sortedTargets[sortedTargets.length - 1];
        
                if (lastTarget && typeof lastTarget.price !== 'undefined' && lastTarget.price !== null && String(lastTarget.price).trim() !== '') {
                    const parsedPrice = parseFloat(lastTarget.price);
                    if (!isNaN(parsedPrice) && isFinite(parsedPrice)) { 
                        tpValueForPayload = parsedPrice;
                    } else {
                        console.error("Multi-TP: Could not parse price for the last partial profit target or price is invalid. Price was:", lastTarget.price, ". Defaulting TP for payload to 0.");
                    }
                } else {
                    console.error("Multi-TP: Last partial profit target or its price is missing/invalid. Defaulting TP for payload to 0.");
                }
            } else {
                console.error("Multi-TP: 'targets' array is empty or undefined. Defaulting TP for payload to 0.");
            }
        } else {
            // Single TP mode
            tpValueForPayload = parseFloat(takeProfitPrice);
        }
        
        // Final safety check for NaN/Infinity for all modes
        if (isNaN(tpValueForPayload) || !isFinite(tpValueForPayload)) {
            console.error("Invalid take_profit value after processing for payload:", tpValueForPayload, "Setting to 0.");
            tpValueForPayload = 0;
        }
        
        console.log("Final Take Profit Price for payload:", tpValueForPayload);

        setLoading(true);
    
        const { slDistance } = calcStopLoss({
            stopLossMode,
            stopLossValue,
            tradeDirection,
            askPrice,
            bidPrice,
            pipSize,
            entryPrice: orderType === "LIMIT" ? parseFloat(limitEntryPrice) : null
        });

    const stopLossDistanceFloat = slDistance !== "N/A" ? parseFloat(slDistance) : null;
        
    // Ensure lot size adheres to lot_step and bounds
    const rawLot = parseFloat(lotSize);
    const roundedLotForPayload = roundToStepNumber(rawLot, lotStep ?? 0.01, 'nearest', minLot, maxLot);
    console.log("lot size (raw, rounded):", rawLot, roundedLotForPayload);
        console.log("Trade Execution Received - Projected Profit:", projectedProfit);
        console.log("Trade Execution Received - Projected Loss:", projectedLoss);
        // Calculate TP distance with robust inputs and fallbacks
        const stopLossForTp = Number.isFinite(stopLossDistanceFloat)
            ? Number(stopLossDistanceFloat)
            : (stopLossDistance != null && !isNaN(Number(stopLossDistance)) ? Number(stopLossDistance) : null);
        const pip = pipSize != null ? Number(pipSize) : null;
        const valueForMode = tpMode === 'price' ? takeProfitPrice : (tpMode === 'distance' ? takeProfitValue : rrRatio);

        const { tpDistance } = calcTakeProfit({
            takeProfitMode: tpMode,
            takeProfitValue: valueForMode,
            stopLossDistance: stopLossForTp,
            tradeDirection,
            askPrice,
            bidPrice,
            pipSize: pip,
            entryPrice: orderType === 'LIMIT' ? parseFloat(limitEntryPrice) : null,
        });

        let tpDist = tpDistance;
        if (tpDist == null || isNaN(tpDist)) {
            // Fallbacks per mode
            if (tpMode === 'rr_ratio' && Number.isFinite(Number(rrRatio)) && Number.isFinite(stopLossForTp)) {
                tpDist = Number(rrRatio) * stopLossForTp;
            } else if (tpMode === 'price' && Number.isFinite(entryPrice) && Number.isFinite(pip)) {
                const tpPriceNum = Number(takeProfitPrice);
                if (Number.isFinite(tpPriceNum)) {
                    tpDist = Math.abs(tpPriceNum - entryPrice) / pip;
                }
            } else if (tpMode === 'distance' && Number.isFinite(Number(takeProfitValue))) {
                tpDist = Number(takeProfitValue);
            }
        }
        console.log("TP DISTANCE (computed, with fallback):", tpDistance, "=>", tpDist);
        const slDistanceValue = Number.isFinite(stopLossDistanceFloat)
            ? Number(stopLossDistanceFloat)
            : (stopLossDistance != null && Number.isFinite(Number(stopLossDistance))
                ? Number(stopLossDistance)
                : null);
        const tpDistanceValue = (tpDist != null && isFinite(tpDist)) ? Number(tpDist) : null;

        // ✅ Prepare trade payload with R:R ratio
        const tradeData = {
            account_id: selectedAccount,
            symbol: selectedSymbol,
            lot_size: roundedLotForPayload,
            direction: tradeDirection,
            order_type: orderType,  
            limit_price: entryPrice.toFixed(5),
            risk_percent: parseFloat(riskPercent),
            stop_loss_distance: slDistanceValue,
            take_profit: tpValueForPayload.toFixed(5), // Use the determined value
            tp_distance: tpDistanceValue,
            rr_ratio: rrRatio ? parseFloat(rrRatio) : null,  // ✅ New field added
            projected_profit: projectedProfit ?? 0,
            projected_loss: projectedLoss ?? 0,     
            atr_multiplier: stopLossType === "atr" ? atrMultiplier : null,
            reason,
            source: "MANUAL",
            //rr_ratio: rrRatio ? parseFloat(rrRatio) : null,
        };
        
            // Attach partial profit block if enabled
        if (profitMode === 'multi') {
            tradeData.partial_profit = true;
            console.log("*********Partial profit enabled. Preparing targets:");
            console.log("Partial profit targets:", targets);
            tradeData.targets = targets
            .sort((a, b) => a.rank - b.rank)
            .map((t) => ({
                rank: t.rank,
                share: t.share,
                tp_type: t.tp_type,
                ...(t.tp_type === 'RR' ? { rr: t.rr } : {}),
                ...(t.tp_type === 'ATR' ? { atr_multiplier: t.atr_multiplier } : {}),
                ...(t.tp_type === 'PRICE' ? { price: t.price } : {}),
            }));
        }
        console.log("Sending trade execution request:", tradeData);
    
        try {
            const response = await executeTrade(tradeData);
            toast.success("Trade executed successfully!");
            console.log("Trade execution response:", response);
            //setModalOpen(false);
          
            if (response.trade_id) {
              setExecutedTradeId(response.trade_id);
              setShowJournalModal(true);
            }
          } catch (error) {
            console.error("Trade execution failed:", error.message);
            toast.error(error.message);
          }
    
        setLoading(false);
    };
    
    return (
        <div className="text-textPrimary"> {/* Ensure container text color is themed */}
            <Button
                type="button"
                variant={isSell ? "destructive" : "success"}
                size={buttonSize}
                className={buttonClassName}
                onClick={handleTrade} 
                disabled={!lotSize || loading}
                aria-label={loading ? `${actionLabel} in progress` : actionLabel}
            >
                {loading ? (
                    <Loader2 className={`${iconClasses} animate-spin`} aria-hidden="true" />
                ) : (
                    <SendHorizontal className={iconClasses} aria-hidden="true" />
                )}
                {loading ? "Executing..." : actionLabel}
            </Button>
        </div>
    );
};

export default TradeExecution;
